// A session that has ended, and a local sign-out (Session ASB, Item 3): the two small signals the fetch layer (api.ts)
// and the auth context (auth.tsx) exchange. Kept in their own module, with no imports, so each side depends on the
// signal rather than on the other's whole surface.
//
// WHY THE SESSION-ENDED SIGNAL EXISTS. Sessions live on the server now, with an idle limit and an absolute limit per
// role, and can be ended from elsewhere (sign out everywhere, an administrator, a deactivation, a password reset). When
// one ends, the NEXT request answers 401 with the guard's own body, { error: "unauthorized", reason } (or
// { error: "auditor_expired" }), and every screen needs to react the same way: sign out locally and go to the sign-in
// page with a plain-English reason. So the one fetch primitive (and the raw file downloads) report it here, and
// AuthProvider is the listener that acts on it.
//
// Only the GUARD'S 401s count. /portal/ has its own cookie and its own handling, and /public/ token routes answer 401
// for their own reasons (a wrong link, an expired one); a login's own "those details did not match" is
// invalid_credentials, not unauthorized. Each event carries when its request STARTED, so a 401 for a request sent
// before the person signed in again can be told apart and ignored.
//
// The reasons the guard sends today (server/src/modules/auth/guards.ts), and the auditor policy's. A reason a newer
// server adds is passed through AS IT CAME (sessionEndReasonOf, below), so the type admits any other word too.
export type SessionEndReason =
  | "idle_timeout"
  | "session_expired"
  | "signed_out"
  | "no_session"
  | "auditor_expired"
  // Any other word (written this way so the five above are still offered by the editor).
  | (string & {});
export interface SessionEndedEvent {
  reason: SessionEndReason;
  startedAt: number;
}

// THE ONE RULE FOR REASONS (Session ASB, fix round 4): the reasons a session RUNS OUT rather than being taken away. The
// only list either layer that decides the fate of the offline copies reads: the auth layer (auth.tsx keepsOfflineCopies)
// and the history cache (offline/historyCache.ts isRevocation). An allow-list, so every other reason WIPES: a session
// taken away (signed_out), an ended auditor window, and any reason this bundle has never heard of, until somebody decides
// otherwise here.
export const RAN_OUT_END_REASONS: ReadonlySet<string> = new Set<SessionEndReason>(["idle_timeout", "session_expired", "no_session"]);

// A reason as the guard writes one: a lower-case word of letters, digits and underscores.
const PLAIN_REASON = /^[a-z][a-z0-9_]{0,63}$/;

// What a 401's body says about the session: the ONE reading, used by reportIfSessionEnded below and by the history
// cache's isRevocation. Null when the body is not the guard's answer at all (a login's own "those details did not
// match", a body that is not JSON): nothing about the session to act on. Otherwise why the session ended:
//   - { error: "auditor_expired" } is the auditor policy's;
//   - { error: "unauthorized" } with NO reason (tenantScoped's) reads as no_session;
//   - any other reason is passed through AS THE SERVER SENT IT, never folded into one this bundle knows, so a reason the
//     server adds later reaches the auth layer as itself and, not being one that ran out, wipes;
//   - a reason that is not a plain word (not text, empty, or anything else) cannot be read, and reads as signed_out, a
//     session taken away: it wipes too, and nothing odd reaches the sign-in page's address (ProtectedRoute's ?reason=).
export function sessionEndReasonOf(body: unknown): SessionEndReason | null {
  if (!body || typeof body !== "object") return null;
  const { error, reason } = body as { error?: unknown; reason?: unknown };
  if (error === "auditor_expired") return "auditor_expired";
  if (error !== "unauthorized") return null;
  if (reason === undefined) return "no_session";
  return typeof reason === "string" && PLAIN_REASON.test(reason) ? reason : "signed_out";
}

const sessionEndedListeners = new Set<(event: SessionEndedEvent) => void>();

export function onSessionEnded(listener: (event: SessionEndedEvent) => void): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

// Tell the listeners when a response is the guard saying the session is over. `path` is the API path without its
// /api prefix.
export function reportIfSessionEnded(path: string, status: number, body: unknown, startedAt: number): void {
  if (status !== 401) return;
  // Session ATA: the Subcontractor Portal has its own cookie too, and its 401 must never sign a staff session out here.
  if (path.startsWith("/portal/") || path.startsWith("/public/") || path.startsWith("/subcontractor-portal/")) return;
  const ended = sessionEndReasonOf(body);
  if (ended === null) return;
  for (const listener of [...sessionEndedListeners]) {
    try {
      listener({ reason: ended, startedAt });
    } catch {
      // A listener's own failure never turns into the request's failure.
    }
  }
}

// ---- Idle that is really idle (Session ASB review, finding F4) ----
//
// A request the browser makes on a TIMER (a poll) sends this header, and the server then serves it without counting it
// as activity: the session's idle clock (60 minutes for office, admin and auditor, D-SEC-4) only moves for something a
// person did. api.ts adds it for `background: true`; a person's own action never carries it.
export const BACKGROUND_REQUEST_HEADER = "X-ELE-Background";

// When this browser last did something that counts as activity on its session: a request the server answered that was
// not a background one. Kept in localStorage so every tab of the browser shares it (they share one cookie, so one
// session), with an in-memory copy for a browser whose storage is unavailable. The office shell's idle warning reads it
// (IdleSessionWarning). A HINT only: the server's own lastSeenAt is what ends a session.
const ACTIVITY_KEY = "ele.auth.lastActivityAt";
let lastActivityInMemory = 0;

export function noteSessionActivity(at: number = Date.now()): void {
  if (at <= lastActivityInMemory) return;
  lastActivityInMemory = at;
  try {
    const stored = Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
    if (at > stored) localStorage.setItem(ACTIVITY_KEY, String(at));
  } catch {
    // Private mode or storage disabled: the in-memory copy above still serves this tab.
  }
}

export function lastSessionActivityAt(): number {
  let stored = 0;
  try {
    stored = Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
  } catch {
    stored = 0;
  }
  return Math.max(stored, lastActivityInMemory);
}

// Test seam: forget the in-memory copy (a test clears localStorage itself).
export function forgetSessionActivityForTests(): void {
  lastActivityInMemory = 0;
}

// THE LOCAL SIGN-OUT SIGNAL: the auth context announces that this browser has signed out (whether the person chose to
// or the server ended the session), and whatever holds per-session state in memory drops it. api.ts's request cache
// listens (it is keyed by path, not by user, so it must not outlive the session); before ASB it was cleared only as a
// side effect of the logout POST succeeding.
const localSignOutListeners = new Set<() => void>();

export function onLocalSignOut(listener: () => void): () => void {
  localSignOutListeners.add(listener);
  return () => {
    localSignOutListeners.delete(listener);
  };
}

export function announceLocalSignOut(): void {
  for (const listener of [...localSignOutListeners]) {
    try {
      listener();
    } catch {
      // Best-effort: one listener's failure never stops the others, nor the sign-out.
    }
  }
}
